import {
  KeyboardArrowDown,
  ArrowDropUp,
  ArrowDropDown,
} from "@mui/icons-material";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  TableContainer,
  Paper,
} from "@mui/material";
import clsx from "clsx";
import React, { useMemo, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { IWithClassName } from "../../theme/default.theme";
import { HeaderFormatEnum } from "./enum";
import ExpandCircleDownIcon from "@mui/icons-material/ExpandCircleDown";

const ITEMS_PER_PAGE_ALL = -1;

export interface ITableColumn {
  title: string;
  id: string;
  sortable: boolean;
  width?: string | number;
  isNumber?: boolean;
}

export interface IOwnProps {
  columns: Array<ITableColumn>;
  rowsPerPage: number;
  totalRows: number;
  currentPage: number;
  onRowsPerPageChange?: (rows: number) => void;
  onPageChange?: (page: number) => void;
  onColumnOrder?: (
    columnIndex: number,
    column: ITableColumn,
    direction: "asc" | "desc",
  ) => void;
  renderCell: (
    rowIndex: number,
    columnIndex: number,
    column: ITableColumn,
  ) => React.ReactNode;
  customHeader?: React.ReactNode;
  headerFormat?: HeaderFormatEnum;
  uniqueRowId: (rowIndex: number) => string;
  isLoading?: boolean;
  children?: React.ReactNode;
}

const TableComponent = ({
  className,
  columns,
  rowsPerPage,
  totalRows,
  currentPage,
  onRowsPerPageChange,
  onPageChange,
  onColumnOrder,
  renderCell,
  customHeader,
  headerFormat,
  uniqueRowId,
  isLoading,
  children,
}: IWithClassName & IOwnProps) => {
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | null>(
    null,
  );
  const [sortedColumnIndex, setSortedColumnIndex] = useState<number | null>(
    null,
  );
  const totalPages =
    rowsPerPage === ITEMS_PER_PAGE_ALL ? 1 : Math.ceil(totalRows / rowsPerPage);
  const baseRowCalculation = (currentPage - 1) * rowsPerPage;
  const rowsLeft = totalRows - baseRowCalculation;
  const rowsToDisplay =
    rowsPerPage === ITEMS_PER_PAGE_ALL
      ? totalRows
      : Math.min(rowsPerPage, rowsLeft);

  const handleColumnSort = (columnIndex: number, column: ITableColumn) => {
    const newDirection = sortDirection === "asc" ? "desc" : "asc";
    setSortDirection(newDirection);
    setSortedColumnIndex(columnIndex);
    onColumnOrder?.(columnIndex, column, newDirection);
  };

  const loadingRows = useMemo(
    () => (totalRows > 0 && rowsPerPage > totalRows ? totalRows : rowsPerPage),
    [rowsPerPage, totalRows],
  );

  const handleRowsPerPageChange = (
    event: SelectChangeEvent<number | "All">,
  ) => {
    const newValue = event.target.value;
    if (newValue === "All") {
      onRowsPerPageChange?.(ITEMS_PER_PAGE_ALL);
    } else {
      onRowsPerPageChange?.(newValue as number);
    }
  };

  const displayRangeStart = baseRowCalculation + 1;
  const displayRangeEnd =
    rowsPerPage === ITEMS_PER_PAGE_ALL
      ? totalRows
      : Math.min(baseRowCalculation + rowsPerPage, totalRows);

  return (
    <TableContainer
      className={clsx("table-container", className)}
      component={Paper}
    >
      <Box className="header-card">
        <Typography component="h6">{customHeader}</Typography>
        {children}
      </Box>
      <Table className="content-table">
        <TableHead>
          <TableRow>
            {columns.map((column, columnIndex) => (
              <TableCell
                key={column.id}
                onClick={() =>
                  column.sortable && handleColumnSort(columnIndex, column)
                }
                className={clsx(
                  column.sortable ? "sortable" : "",
                  `columnWidth-${column.id}`,
                )}
              >
                <div className="header-cell">
                  <Typography>{column.title}</Typography>
                  {sortedColumnIndex === columnIndex &&
                    (sortDirection === "asc" ? (
                      <ArrowDropUp className="sortIcon" />
                    ) : (
                      <ArrowDropDown className="sortIcon" />
                    ))}
                </div>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading
            ? Array.from({
                length: rowsPerPage === ITEMS_PER_PAGE_ALL ? 50 : loadingRows,
              }).map((_, rowIndex) => (
                <TableRow key={uniqueRowId(baseRowCalculation + rowIndex)}>
                  {columns.map((column) => (
                    <TableCell key={column.id}>
                      <Skeleton />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : Array.from({ length: rowsToDisplay }).map((_, rowIndex) => (
                <TableRow key={uniqueRowId(baseRowCalculation + rowIndex)}>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      className={`columnWidth-${column.id}`}
                    >
                      <Box className="cell-content">
                        {renderCell(
                          baseRowCalculation + rowIndex,
                          columns.indexOf(column),
                          column,
                        )}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              ))}
        </TableBody>
      </Table>
      <Box className="table-footer">
        <Box className="pagination">
          <Button
            onClick={() => onPageChange?.(currentPage - 1)}
            disabled={currentPage <= 1}
            aria-label="Previous page"
          >
            <ExpandCircleDownIcon className="prev-icon" />
          </Button>
          <span>
            {displayRangeStart}-{displayRangeEnd} of {totalRows}
          </span>
          <Button
            onClick={() => onPageChange?.(currentPage + 1)}
            disabled={currentPage >= totalPages}
            aria-label="Next page"
          >
            <ExpandCircleDownIcon className="next-icon" />
          </Button>
        </Box>
        <Box className="rows-per-page-container">
          <FormControl variant="outlined">
            <FormLabel component="legend">Results per page</FormLabel>
            <Select
              variant="outlined"
              size="small"
              label="Results per page"
              onChange={handleRowsPerPageChange}
              value={rowsPerPage === ITEMS_PER_PAGE_ALL ? "All" : rowsPerPage}
              IconComponent={KeyboardArrowDown}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
              <MenuItem value={200}>200</MenuItem>
              <MenuItem value="All">All</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>
    </TableContainer>
  );
};

export default TableComponent;
